// Punctul unic prin care trece orice cerere către API. Reînnoiește tokenul
// automat la 401 și, dacă nici refresh-ul nu mai merge, anunță aplicația să
// revină la ecranul de autentificare.

import { tr, limba, trMesajServer } from "./i18n.js";

const API_BASE =
  import.meta.env.VITE_MENTENANTA_API_BASE || "http://localhost:3002/api";

const CHEIE_TOKENURI = "mentenanta_tokens";

function citesteTokenuri() {
  try {
    return JSON.parse(localStorage.getItem(CHEIE_TOKENURI) || "null");
  } catch {
    return null;
  }
}

export function scrieTokenuri(t) {
  if (t) localStorage.setItem(CHEIE_TOKENURI, JSON.stringify(t));
  else localStorage.removeItem(CHEIE_TOKENURI);
}

export function areSesiune() {
  return !!citesteTokenuri()?.accessToken;
}

let laSesiuneExpirata = () => {};
export function candExpiraSesiunea(fn) {
  laSesiuneExpirata = fn;
}

/**
 * Traduce mesajele implicite ale validatorului din backend, care sunt în
 * engleză. Un mesaj netradus rămâne cum e — nu ascundem nimic, doar nu
 * îmbunătățim ce nu recunoaștem.
 */
function mesajRomanesc(text) {
  const tipare = [
    [/must be an email$/i, "Adresa de email nu are un format valid."],
    [/must be longer than or equal to (\d+) characters$/i, "Trebuie să aibă cel puțin $1 caractere."],
    [/should not be empty$/i, "Câmpul este obligatoriu."],
    [/must be a UUID$/i, "Identificator invalid."],
    [/must be one of the following values: (.+)$/i, "Valoare permisă: $1."],
    [/property (\w+) should not exist$/i, "Câmpul „$1” nu este acceptat."],
    [/must be a boolean value$/i, "Valoarea trebuie să fie da sau nu."],
  ];
  for (const [re, inlocuire] of tipare) {
    if (re.test(text)) return text.replace(re, inlocuire);
  }
  return text;
}

/**
 * Pagina de acceptare a unei oferte NU trece prin `cerere`: cine deschide
 * linkul din email nu are cont, deci n-are ce token să trimită și n-are ce
 * reînnoi. Tokenul din link e singura cheie.
 */
async function faraSesiune(cale, optiuni = {}) {
  const r = await fetch(API_BASE + cale, optiuni);
  const text = await r.text();
  const date = text ? JSON.parse(text) : null;
  if (!r.ok) throw { mesaj: date?.message ?? tr("Cererea a eșuat."), status: r.status };
  return date;
}

export const apiPublic = {
  oferta: (token) => faraSesiune(`/oferte/publica/${token}`),
  decide: (token, accept, corp) =>
    faraSesiune(`/oferte/publica/${token}/${accept ? "accepta" : "refuza"}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(corp),
    }),
};

/**
 * Mesajul de eroare, în limba aleasă.
 *
 * Două traduceri în direcții opuse, și ordinea contează: validatorul din
 * backend vorbește ENGLEZĂ (mesaje implicite `class-validator`), restul
 * serverului vorbește ROMÂNĂ. În română se traduce primul; în engleză,
 * al doilea — iar mesajele validatorului se lasă cum sunt, că sunt deja
 * englezești.
 */
function unMesaj(text) {
  const s = String(text);
  return limba() === "en" ? trMesajServer(s) : mesajRomanesc(s);
}

/**
 * Eșecul e din lipsă de SEMNAL, nu un refuz al serverului?
 *
 * `cerere` aruncă `{status, mesaj}` pentru orice răspuns HTTP; când rețeaua
 * nici n-a ajuns la server, `fetch` aruncă un `TypeError` fără status. Ăsta e
 * singurul mod cinstit de a le deosebi — iar deosebirea contează: un 400 se
 * repară schimbând datele, o lipsă de semnal se repară ieșind din hală.
 */
export function esteFaraSemnal(e) {
  if (e && typeof e === 'object' && 'status' in e) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  /*
   * NU orice `TypeError` — doar cel al lui `fetch`.
   *
   * Un `TypeError` de programare („x is not a function") ar fi altfel îmbrăcat
   * în „s-a salvat în telefon", iar defectul ar rămâne nevăzut, cu omul convins
   * că munca lui pleacă singură. Fiecare browser își scrie altfel eșecul de
   * rețea: Chrome „Failed to fetch", Firefox „NetworkError…", Safari „Load
   * failed" — de-aia se caută tiparul, nu un text anume.
   */
  const m = String(e?.message ?? e);
  return e instanceof TypeError
    && /failed to fetch|networkerror|load failed|network request failed/i.test(m);
}

export function mesajEroare(e) {
  const m = e?.mesaj ?? e?.message ?? tr("A apărut o eroare.");
  return Array.isArray(m) ? m.map(unMesaj).join(" ") : unMesaj(m);
}

async function cerere(cale, optiuni = {}, permiteRefresh = true) {
  const tokenuri = citesteTokenuri();
  const headers = { "Content-Type": "application/json", ...(optiuni.headers || {}) };
  if (tokenuri?.accessToken) headers.Authorization = `Bearer ${tokenuri.accessToken}`;

  const r = await fetch(`${API_BASE}${cale}`, { ...optiuni, headers });

  if (r.status === 401 && permiteRefresh && tokenuri?.refreshToken) {
    const rr = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: tokenuri.refreshToken }),
    });
    if (rr.ok) {
      const noi = await rr.json();
      scrieTokenuri({ accessToken: noi.accessToken, refreshToken: noi.refreshToken });
      return cerere(cale, optiuni, false);
    }
    scrieTokenuri(null);
    laSesiuneExpirata();
    throw { mesaj: tr("Sesiunea a expirat. Autentifică-te din nou.") };
  }

  if (!r.ok) {
    let corp = {};
    try { corp = await r.json(); } catch { /* răspuns fără JSON */ }
    throw { status: r.status, mesaj: corp.message || tr("Eroare {0}", r.status) };
  }
  return r.status === 204 ? null : r.json();
}

export const api = {
  /**
   * Autentificarea, cu răbdare pentru un server care tocmai se trezește.
   *
   * Pe planul gratuit, serviciul adoarme după un timp de inactivitate, iar
   * prima cerere de dimineață poate dura peste un minut. Fără asta, omul
   * primea „Failed to fetch" — un mesaj care nu spune nici ce s-a întâmplat,
   * nici ce are de făcut, pe o aplicație care de fapt e în regulă.
   *
   * Se reîncearcă DOAR la un eșec de REȚEA. Un refuz al serverului — parolă
   * greșită, cont dezactivat — se ridică din prima: a-l reîncerca de opt ori
   * ar întârzia degeaba răspunsul și ar arde încercările de rată-limită.
   *
   * @param seAsteapta chemat când cererea a picat din rețea și se mai încearcă
   *   o dată, ca ecranul să poată spune de ce durează.
   */
  login: async (email, parola, seAsteapta) => {
    const corp = { method: "POST", body: JSON.stringify({ email, parola }) };
    for (let incercare = 0; ; incercare++) {
      try {
        return await cerere("/auth/login", corp, false);
      } catch (e) {
        const eRetea = e instanceof TypeError
          || /failed to fetch|networkerror|load failed|network request failed/i.test(String(e?.message ?? ""));
        if (!eRetea || incercare >= 8) throw e;
        if (seAsteapta) seAsteapta(incercare + 1);
        await new Promise((r) => setTimeout(r, 5000));
      }
    }
  },
  eu: () => cerere("/auth/me"),
  logout: () => cerere("/auth/logout", { method: "POST" }),
  utilizatori: () => cerere("/utilizatori"),
  creeazaUtilizator: (d) => cerere("/utilizatori", { method: "POST", body: JSON.stringify(d) }),
  modificaUtilizator: (id, d) =>
    cerere(`/utilizatori/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  dezactiveazaUtilizator: (id) => cerere(`/utilizatori/${id}`, { method: "DELETE" }),

  // MT20.4 — matricea de drepturi a firmei.
  drepturi: () => cerere("/drepturi"),
  /** Drepturile PROPRII, ca ecranele să-și arate butoanele după matrice. */
  drepturileMele: () => cerere("/drepturi/ale-mele"),

  // Puntea de sesizări tehnice (docs/Contract_Sesizari_Tehnice.md).
  //
  // Maparea aparține NOUĂ, consumatorului: emițătorul trimite referința lui,
  // iar legătura cu clientul și locația se ține aici. De-aia ecranul e al
  // firmei, nu al celui care trimite.
  integrariChei: () => cerere("/integrari/chei"),
  integrariCreeazaCheie: (d) =>
    cerere("/integrari/chei", { method: "POST", body: JSON.stringify(d) }),
  integrariRevocaCheie: (id) => cerere(`/integrari/chei/${id}`, { method: "DELETE" }),

  integrariMapari: (cheieApiId) =>
    cerere(`/integrari/mapari${cheieApiId ? `?cheieApiId=${encodeURIComponent(cheieApiId)}` : ""}`),
  integrariCreeazaMapare: (d) =>
    cerere("/integrari/mapari", { method: "POST", body: JSON.stringify(d) }),
  integrariStergeMapare: (id) => cerere(`/integrari/mapari/${id}`, { method: "DELETE" }),

  /** Cine sună și n-a fost recunoscut. Contractul §6 cere să se VADĂ. */
  integrariNemapate: (toate = false) =>
    cerere(`/integrari/nemapate${toate ? "?toate=true" : ""}`),

  // Supravegherea parcului: unde se strică ceva anormal de des.
  supraveghereMatrice: () => cerere("/supraveghere/matrice"),
  supraveghereTipare: (f = {}) => {
    const q = new URLSearchParams(
      Object.entries(f).filter(([, v]) => v !== "" && v !== undefined && v !== null),
    ).toString();
    return cerere(`/supraveghere/tipare${q ? `?${q}` : ""}`);
  },
  supraveghereUzura: (f = {}) => {
    const q = new URLSearchParams(
      Object.entries(f).filter(([, v]) => v !== "" && v !== undefined && v !== null),
    ).toString();
    return cerere(`/supraveghere/uzura${q ? `?${q}` : ""}`);
  },
  supraveghereMuta: (clientIds, nivelId) =>
    cerere("/supraveghere/muta", { method: "POST", body: JSON.stringify({ clientIds, nivelId }) }),
  supraveghereSchimbaNivel: (id, d) =>
    cerere(`/supraveghere/niveluri/${id}`, { method: "PATCH", body: JSON.stringify(d) }),

  // §39 — jurisdicția firmei: pachetul de țară activ și alegerile proprii.
  jurnal: (f = {}) => {
    const q = new URLSearchParams(
      Object.entries(f).filter(([, v]) => v !== "" && v !== undefined && v !== null),
    ).toString();
    return cerere(`/jurnal${q ? "?" + q : ""}`);
  },

  jurisdictie: () => cerere("/jurisdictie/stare"),
  jurisdictieInstaleazaLivrat: () => cerere("/jurisdictie/pachet-livrat", { method: "POST" }),
  jurisdictieAlege: (d) =>
    cerere("/jurisdictie/alegeri", { method: "PATCH", body: JSON.stringify(d) }),
  jurisdictieDezactiveaza: (id) =>
    cerere(`/jurisdictie/pachete/${id}/dezactiveaza`, { method: "PATCH" }),
  /** Plicul semnat, exact cum a ieșit din `scripts/semneaza-pachet.js`. */
  jurisdictieImporta: (plic) =>
    cerere("/jurisdictie/pachete/import", { method: "POST", body: JSON.stringify(plic) }),
  aseazaDrepturi: (rol, modul, drepturi) =>
    cerere("/drepturi", { method: "POST", body: JSON.stringify({ rol, modul, drepturi }) }),
  drepturiLaImplicit: (modul) =>
    cerere("/drepturi/revino-la-implicit", { method: "POST", body: JSON.stringify({ modul }) }),

  // ── Nomenclator (MT01) ──────────────────────────────────────────────────
  arbore: (inactive = false) => cerere(`/nomenclator/arbore${inactive ? "?inactive=true" : ""}`),
  cauta: (q) => cerere(`/nomenclator/cauta?q=${encodeURIComponent(q)}`),
  creeazaSistem: (d) => cerere("/nomenclator/sisteme", { method: "POST", body: JSON.stringify(d) }),
  creeazaSubsistem: (d) => cerere("/nomenclator/subsisteme", { method: "POST", body: JSON.stringify(d) }),
  creeazaTip: (d) => cerere("/nomenclator/tipuri", { method: "POST", body: JSON.stringify(d) }),
  propuneTip: (d) => cerere("/nomenclator/tipuri/propune", { method: "POST", body: JSON.stringify(d) }),
  propuneri: () => cerere("/nomenclator/propuneri"),
  confirmaPropunere: (id) => cerere(`/nomenclator/tipuri/${id}/confirma`, { method: "PATCH" }),
  respingePropunere: (id, motiv) =>
    cerere(`/nomenclator/tipuri/${id}/respinge`, { method: "PATCH", body: JSON.stringify({ motiv }) }),
  fuzioneazaPropunere: (id, catreId) =>
    cerere(`/nomenclator/tipuri/${id}/fuzioneaza`, { method: "PATCH", body: JSON.stringify({ catreId }) }),
  dezactiveazaTip: (id) => cerere(`/nomenclator/tipuri/${id}/dezactiveaza`, { method: "PATCH" }),
  reactiveazaTip: (id) => cerere(`/nomenclator/tipuri/${id}/reactiveaza`, { method: "PATCH" }),

  // ── Catalog de articole (MT02) ──────────────────────────────────────────
  /**
   * Întoarce `{ articole, total, plafon }` — lista e plafonată pe server, iar
   * ecranul trebuie să poată spune asta. Căutarea se trimite tot acolo: peste
   * douăzeci de mii de poziții, o filtrare în browser ar căuta doar în ce a
   * apucat să vină.
   */
  articole: (tip, familie, inactive, cauta) => {
    const p = new URLSearchParams();
    if (tip) p.set("tip", tip);
    if (familie) p.set("familie", familie);
    if (inactive) p.set("inactive", "true");
    if (cauta) p.set("cauta", cauta);
    const q = p.toString();
    return cerere(`/catalog/articole${q ? "?" + q : ""}`);
  },
  /** Pentru ecranele care au nevoie doar de listă, fără plafonul afișat. */
  articoleLista: async (tip, familie, inactive, cauta) =>
    (await api.articole(tip, familie, inactive, cauta)).articole ?? [],
  familiiCatalog: (tip) => cerere(`/catalog/familii${tip ? "?tip=" + tip : ""}`),
  starePreturi: () => cerere("/catalog/stare-preturi"),
  creeazaArticol: (d) => cerere("/catalog/articole", { method: "POST", body: JSON.stringify(d) }),
  schimbaPret: (id, d) =>
    cerere(`/catalog/articole/${id}/pret`, { method: "PATCH", body: JSON.stringify(d) }),
  dezactiveazaArticol: (id) =>
    cerere(`/catalog/articole/${id}/dezactiveaza`, { method: "PATCH" }),
  reactiveazaArticol: (id) =>
    cerere(`/catalog/articole/${id}/reactiveaza`, { method: "PATCH" }),
  importaCatalog: (tip, linii, aplica) =>
    cerere("/catalog/import", { method: "POST", body: JSON.stringify({ tip, linii, aplica }) }),

  // ── Operații tehnice (MT03) ─────────────────────────────────────────────
  operatii: ({ tipComponenta, sistem, categorie, toate, inactive } = {}) => {
    const p = new URLSearchParams();
    if (tipComponenta) p.set("tipComponenta", tipComponenta);
    if (sistem) p.set("sistem", sistem);
    if (categorie) p.set("categorie", categorie);
    if (toate) p.set("toate", "true");
    if (inactive) p.set("inactive", "true");
    const q = p.toString();
    return cerere(`/operatii${q ? "?" + q : ""}`);
  },
  statisticiOperatii: () => cerere("/operatii/statistici"),
  creeazaOperatie: (d) => cerere("/operatii", { method: "POST", body: JSON.stringify(d) }),
  schimbaNorma: (id, d) => cerere(`/operatii/${id}/norma`, { method: "PATCH", body: JSON.stringify(d) }),
  dezactiveazaOperatie: (id) => cerere(`/operatii/${id}/dezactiveaza`, { method: "PATCH" }),
  reactiveazaOperatie: (id) => cerere(`/operatii/${id}/reactiveaza`, { method: "PATCH" }),

  // ── Clienți, locații, contracte (MT04) ──────────────────────────────────
  clienti: ({ q, statut, inactive } = {}) => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (statut) p.set("statut", statut);
    if (inactive) p.set("inactive", "true");
    const s = p.toString();
    return cerere(`/clienti${s ? "?" + s : ""}`);
  },
  client: (id) => cerere(`/clienti/${id}`),
  inroleazaClient: (d) => cerere("/clienti/inrolare", { method: "POST", body: JSON.stringify(d) }),
  creeazaClient: (d) => cerere("/clienti", { method: "POST", body: JSON.stringify(d) }),
  actualizeazaClient: (id, d) =>
    cerere(`/clienti/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  schimbaStatutClient: (id, statut) =>
    cerere(`/clienti/${id}/statut`, { method: "PATCH", body: JSON.stringify({ statut }) }),
  locatii: (clientId, inactive) =>
    cerere(`/clienti/${clientId}/locatii${inactive ? "?inactive=true" : ""}`),
  creeazaLocatie: (clientId, d) =>
    cerere(`/clienti/${clientId}/locatii`, { method: "POST", body: JSON.stringify(d) }),
  actualizeazaLocatie: (id, d) =>
    cerere(`/clienti/locatii/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  comutaLocatie: (id, activ) =>
    cerere(`/clienti/locatii/${id}/activ`, { method: "PATCH", body: JSON.stringify({ activ }) }),
  creeazaContact: (clientId, d, locatieId) =>
    cerere(`/clienti/${clientId}/contacte${locatieId ? "?locatie=" + locatieId : ""}`,
      { method: "POST", body: JSON.stringify(d) }),
  stergeContact: (id) => cerere(`/clienti/contacte/${id}`, { method: "DELETE" }),
  creeazaContract: (clientId, d) =>
    cerere(`/clienti/${clientId}/contracte`, { method: "POST", body: JSON.stringify(d) }),
  actualizeazaContract: (id, d) =>
    cerere(`/clienti/contracte/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  comutaContract: (id, activ) =>
    cerere(`/clienti/contracte/${id}/activ`, { method: "PATCH", body: JSON.stringify({ activ }) }),
  contracteCareExpira: (zile = 60) => cerere(`/clienti/contracte-care-expira?zile=${zile}`),

  // ── Parc instalat (MT05) ────────────────────────────────────────────────
  parc: (clientId, inactive) =>
    cerere(`/parc/client/${clientId}${inactive ? "?inactive=true" : ""}`),
  cautaEchipament: (q, clientId) => {
    const p = new URLSearchParams({ q });
    if (clientId) p.set("client", clientId);
    return cerere(`/parc/cauta?${p.toString()}`);
  },
  scadentar: (zile = 30, clientId) => {
    const p = new URLSearchParams({ zile: String(zile) });
    if (clientId) p.set("client", clientId);
    return cerere(`/parc/scadentar?${p.toString()}`);
  },
  creeazaSistemInstalat: (clientId, d) =>
    cerere(`/parc/client/${clientId}/sisteme`, { method: "POST", body: JSON.stringify(d) }),
  actualizeazaSistemInstalat: (id, d) =>
    cerere(`/parc/sisteme/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  comutaSistemInstalat: (id, activ) =>
    cerere(`/parc/sisteme/${id}/activ`, { method: "PATCH", body: JSON.stringify({ activ }) }),
  adaugaEchipament: (sistemId, d) =>
    cerere(`/parc/sisteme/${sistemId}/echipamente`, { method: "POST", body: JSON.stringify(d) }),
  echipament: (id) => cerere(`/parc/echipamente/${id}`),
  scoateDinFunctiune: (id, d) =>
    cerere(`/parc/echipamente/${id}/scoate-din-functiune`, { method: "PATCH", body: JSON.stringify(d) }),

  // ── Tehnicieni, echipe, alocare (MT07) ──────────────────────────────────
  tehnicieni: ({ q, zona, inactivi } = {}) => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (zona) p.set("zona", zona);
    if (inactivi) p.set("inactivi", "true");
    const s = p.toString();
    return cerere(`/echipe/tehnicieni${s ? "?" + s : ""}`);
  },
  creeazaTehnician: (d) => cerere("/echipe/tehnicieni", { method: "POST", body: JSON.stringify(d) }),
  actualizeazaTehnician: (id, d) =>
    cerere(`/echipe/tehnicieni/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  acordaCalificare: (id, d) =>
    cerere(`/echipe/tehnicieni/${id}/calificari`, { method: "POST", body: JSON.stringify(d) }),
  retrageCalificare: (id, calificareId) =>
    cerere(`/echipe/tehnicieni/${id}/calificari/${calificareId}`, { method: "DELETE" }),
  calificariCareExpira: (zile = 60) => cerere(`/echipe/calificari-care-expira?zile=${zile}`),
  indisponibilitati: (tehnicianId) =>
    cerere(`/echipe/indisponibilitati${tehnicianId ? "?tehnician=" + tehnicianId : ""}`),
  adaugaIndisponibilitate: (id, d) =>
    cerere(`/echipe/tehnicieni/${id}/indisponibilitati`, { method: "POST", body: JSON.stringify(d) }),
  stergeIndisponibilitate: (id) =>
    cerere(`/echipe/indisponibilitati/${id}`, { method: "DELETE" }),
  echipe: (inactive) => cerere(`/echipe${inactive ? "?inactive=true" : ""}`),
  creeazaEchipa: (d) => cerere("/echipe", { method: "POST", body: JSON.stringify(d) }),
  comutaEchipa: (id, activ) =>
    cerere(`/echipe/${id}/activ`, { method: "PATCH", body: JSON.stringify({ activ }) }),
  adaugaMembru: (echipaId, d) =>
    cerere(`/echipe/${echipaId}/membri`, { method: "POST", body: JSON.stringify(d) }),
  scoateMembru: (echipaId, tehnicianId) =>
    cerere(`/echipe/${echipaId}/membri/${tehnicianId}`, { method: "DELETE" }),
  alocariClient: (clientId) => cerere(`/echipe/alocari/${clientId}`),
  aloca: (clientId, d) =>
    cerere(`/echipe/alocari/${clientId}`, { method: "POST", body: JSON.stringify(d) }),
  incheieAlocare: (id) => cerere(`/echipe/alocari/${id}/incheie`, { method: "PATCH" }),
  propuneTehnicieni: (d) => cerere("/echipe/propune", { method: "POST", body: JSON.stringify(d) }),

  // ── Stoc și logistică (MT13) ────────────────────────────────────────────
  gestiuni: (inactive) => cerere(`/stoc/gestiuni${inactive ? "?inactive=true" : ""}`),
  creeazaGestiune: (d) => cerere("/stoc/gestiuni", { method: "POST", body: JSON.stringify(d) }),
  deReaprovizionat: () => cerere("/stoc/de-reaprovizionat"),
  solduri: (gestiuneId, subPrag) =>
    cerere(`/stoc/gestiuni/${gestiuneId}/solduri${subPrag ? "?subPrag=true" : ""}`),
  miscari: (gestiuneId, limita = 50) =>
    cerere(`/stoc/gestiuni/${gestiuneId}/miscari?limita=${limita}`),
  setPrag: (gestiuneId, d) =>
    cerere(`/stoc/gestiuni/${gestiuneId}/prag`, { method: "PATCH", body: JSON.stringify(d) }),
  receptie: (gestiuneId, d) =>
    cerere(`/stoc/gestiuni/${gestiuneId}/receptie`, { method: "POST", body: JSON.stringify(d) }),
  transfer: (gestiuneId, d) =>
    cerere(`/stoc/gestiuni/${gestiuneId}/transfer`, { method: "POST", body: JSON.stringify(d) }),
  consumStoc: (gestiuneId, d) =>
    cerere(`/stoc/gestiuni/${gestiuneId}/consum`, { method: "POST", body: JSON.stringify(d) }),
  casare: (gestiuneId, d) =>
    cerere(`/stoc/gestiuni/${gestiuneId}/casare`, { method: "POST", body: JSON.stringify(d) }),
  inventare: (gestiuneId) => cerere(`/stoc/gestiuni/${gestiuneId}/inventare`),
  deschideInventar: (gestiuneId, d) =>
    cerere(`/stoc/gestiuni/${gestiuneId}/inventar`, { method: "POST", body: JSON.stringify(d) }),
  numaraInventar: (id, linii) =>
    cerere(`/stoc/inventare/${id}/numara`, { method: "POST", body: JSON.stringify({ linii }) }),
  diferenteInventar: (id) => cerere(`/stoc/inventare/${id}/diferente`),
  valideazaInventar: (id, observatie) =>
    cerere(`/stoc/inventare/${id}/valideaza`, { method: "PATCH", body: JSON.stringify({ observatie }) }),

  // ── Vizite și fișa de intervenție (MT11, MT12) ──────────────────────────
  vizite: ({ client, status, deLa, panaLa, tehnician } = {}) => {
    const p = new URLSearchParams();
    if (client) p.set("client", client);
    if (status) p.set("status", status);
    if (deLa) p.set("deLa", deLa);
    if (panaLa) p.set("panaLa", panaLa);
    // „eu" se rezolvă pe server, din contul care întreabă.
    if (tehnician) p.set("tehnician", tehnician);
    const s = p.toString();
    return cerere(`/interventii/vizite${s ? "?" + s : ""}`);
  },
  creeazaVizita: (d) => cerere("/interventii/vizite", { method: "POST", body: JSON.stringify(d) }),
  deschideFisa: (vizitaId) =>
    cerere(`/interventii/vizite/${vizitaId}/deschide-fisa`, { method: "POST" }),
  /**
   * MT19 — o fișă lucrată fără semnal, urcată dintr-o bucată.
   *
   * Nu pas cu pas: pe teren pașii s-au întâmplat deja, iar o urcare în cinci
   * cereri s-ar putea opri la a treia și ar lăsa fișa pe jumătate scrisă pe
   * server, fără ca nimeni să știe care jumătate.
   */
  urcaFisaDeTeren: (d) =>
    cerere("/interventii/sincronizare", { method: "POST", body: JSON.stringify(d) }),
  fisa: (id) => cerere(`/interventii/fise/${id}`),
  deviz: (id) => cerere(`/interventii/fise/${id}/deviz`),
  oreFisa: (id, d) => cerere(`/interventii/fise/${id}/ore`, { method: "PATCH", body: JSON.stringify(d) }),
  opresteCronometrul: (id) =>
    cerere(`/interventii/fise/${id}/opreste-cronometrul`, { method: "PATCH" }),
  continutFisa: (id, d) =>
    cerere(`/interventii/fise/${id}/continut`, { method: "PATCH", body: JSON.stringify(d) }),
  adaugaOperatieFisa: (id, d) =>
    cerere(`/interventii/fise/${id}/operatii`, { method: "POST", body: JSON.stringify(d) }),
  actualizeazaOperatieFisa: (id, linieId, d) =>
    cerere(`/interventii/fise/${id}/operatii/${linieId}`, { method: "PATCH", body: JSON.stringify(d) }),
  adaugaArticolFisa: (id, d) =>
    cerere(`/interventii/fise/${id}/articole`, { method: "POST", body: JSON.stringify(d) }),
  adaugaDefectFisa: (id, d) =>
    cerere(`/interventii/fise/${id}/defecte`, { method: "POST", body: JSON.stringify(d) }),
  // Fotografiile de pe teren. Fișierul pleacă în base64, ca la documente.
  adaugaFotografieFisa: (id, d) =>
    cerere(`/interventii/fise/${id}/fotografii`, { method: "POST", body: JSON.stringify(d) }),
  stergeFotografieFisa: (id, fotoId) =>
    cerere(`/interventii/fise/${id}/fotografii/${fotoId}`, { method: "DELETE" }),
  stergeLinieFisa: (id, fel, linieId) =>
    cerere(`/interventii/fise/${id}/${fel}/${linieId}`, { method: "DELETE" }),
  finalizeazaFisa: (id) => cerere(`/interventii/fise/${id}/finalizeaza`, { method: "PATCH" }),
  aprobaDeviz: (id) => cerere(`/interventii/fise/${id}/aproba-deviz`, { method: "POST" }),
  respingeDeviz: (id, motiv) =>
    cerere(`/interventii/fise/${id}/respinge-deviz`, { method: "POST", body: JSON.stringify({ motiv }) }),
  stareAprobareDeviz: () => cerere("/interventii/aprobare-deviz"),
  setAprobareDeviz: (activ) =>
    cerere("/interventii/aprobare-deviz", { method: "PATCH", body: JSON.stringify({ activ }) }),
  semneazaFisa: (id, d) =>
    cerere(`/interventii/fise/${id}/semneaza`, { method: "PATCH", body: JSON.stringify(d) }),
  storneazaFisa: (id, motiv) =>
    cerere(`/interventii/fise/${id}/storneaza`, { method: "PATCH", body: JSON.stringify({ motiv }) }),

  // ── Rețete de intervenție (MT24) ────────────────────────────────────────
  retete: ({ categorie, cauta, inactive } = {}) => {
    const p = new URLSearchParams();
    if (categorie) p.set("categorie", categorie);
    if (cauta) p.set("cauta", cauta);
    if (inactive) p.set("inactive", "true");
    const s = p.toString();
    return cerere(`/retete${s ? "?" + s : ""}`);
  },
  reteta: (id) => cerere(`/retete/${id}`),
  creeazaReteta: (d) => cerere("/retete", { method: "POST", body: JSON.stringify(d) }),
  actualizeazaReteta: (id, d) => cerere(`/retete/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  comutaReteta: (id, activ) =>
    cerere(`/retete/${id}/${activ ? "reactiveaza" : "dezactiveaza"}`, { method: "PATCH" }),
  aplicaReteta: (fisaId, d) =>
    cerere(`/retete/aplica/${fisaId}`, { method: "POST", body: JSON.stringify(d) }),
  retetaDinFisa: (fisaId, d) =>
    cerere(`/retete/din-fisa/${fisaId}`, { method: "POST", body: JSON.stringify(d) }),

  // ── Tichete și SLA (MT09, MT08) ─────────────────────────────────────────
  profileSla: () => cerere("/sla/profile"),
  profilSla: (id) => cerere(`/sla/profile/${id}`),
  tichete: ({ clientId, status, deschise } = {}) => {
    const p = new URLSearchParams();
    if (clientId) p.set("client", clientId);
    if (status) p.set("status", status);
    if (deschise) p.set("deschise", "true");
    const s = p.toString();
    return cerere(`/tichete${s ? "?" + s : ""}`);
  },
  tichet: (id) => cerere(`/tichete/${id}`),
  /** Dosarul intervenției întregi — toate vizitele, nu doar ultima. */
  dosarInterventie: (id) => cerere(`/tichete/${id}/dosar`),
  deschideTichet: (d) => cerere("/tichete", { method: "POST", body: JSON.stringify(d) }),
  preiaTichet: (id) => cerere(`/tichete/${id}/preia`, { method: "PATCH" }),
  confirmaSeveritate: (id, d) =>
    cerere(`/tichete/${id}/severitate`, { method: "PATCH", body: JSON.stringify(d) }),
  propuneVizita: (id, d) =>
    cerere(`/tichete/${id}/propuneri`, { method: "POST", body: JSON.stringify(d) }),
  decideVizita: (id, propunereId, d) =>
    cerere(`/tichete/${id}/propuneri/${propunereId}`, { method: "PATCH", body: JSON.stringify(d) }),
  pauzeazaTichet: (id, motiv) =>
    cerere(`/tichete/${id}/pauzeaza`, { method: "PATCH", body: JSON.stringify({ motiv }) }),
  reiaTichet: (id) => cerere(`/tichete/${id}/reia`, { method: "PATCH" }),
  scrieMesajTichet: (id, d) =>
    cerere(`/tichete/${id}/mesaje`, { method: "POST", body: JSON.stringify(d) }),
  inchideTichet: (id, motiv) =>
    cerere(`/tichete/${id}/inchide`, { method: "PATCH", body: JSON.stringify(motiv ? { motiv } : {}) }),

  // ── Notificări (MT17) ───────────────────────────────────────────────────
  notificari: ({ necitite } = {}) => cerere(`/notificari${necitite ? "?necitite=true" : ""}`),
  numarNotificari: () => cerere("/notificari/numar"),
  citesteNotificarea: (id) => cerere(`/notificari/${id}/citita`, { method: "PATCH" }),
  citesteToateNotificarile: () => cerere("/notificari/toate-citite", { method: "PATCH" }),
  preferinteNotificari: () => cerere("/notificari/preferinte"),
  /** Poate serverul să trimită email? Ecranul nu presupune, întreabă. */
  stareEmail: () => cerere("/notificari/stare-email"),
  schimbaPreferinta: (eveniment, d) =>
    cerere(`/notificari/preferinte/${eveniment}`, { method: "PATCH", body: JSON.stringify(d) }),

  // ── Ofertare (MT27) ─────────────────────────────────────────────────────
  oferte: ({ clientId, status } = {}) => {
    const p = new URLSearchParams();
    if (clientId) p.set("client", clientId);
    if (status) p.set("status", status);
    const s = p.toString();
    return cerere(`/oferte${s ? "?" + s : ""}`);
  },
  /** Ofertele acceptate de client care n-au ajuns încă lucrare programată. */
  ofertaDeProgramat: () => cerere("/oferte/de-programat"),
  oferta: (id) => cerere(`/oferte/${id}`),
  creeazaOferta: (d) => cerere("/oferte", { method: "POST", body: JSON.stringify(d) }),
  actualizeazaOferta: (id, d) => cerere(`/oferte/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  trimiteOferta: (id, d) => cerere(`/oferte/${id}/trimite`, { method: "POST", body: JSON.stringify(d) }),
  refaOferta: (id) => cerere(`/oferte/${id}/refa`, { method: "POST" }),
  aprobaOferta: (id) => cerere(`/oferte/${id}/aproba`, { method: "POST" }),
  respingeOferta: (id, motiv) =>
    cerere(`/oferte/${id}/respinge`, { method: "POST", body: JSON.stringify({ motiv }) }),
  pragAprobare: () => cerere("/oferte/prag-aprobare"),
  setPragAprobare: (d) =>
    cerere("/oferte/prag-aprobare", { method: "PATCH", body: JSON.stringify(d) }),

  // ── Mentenanță preventivă (MT10) ────────────────────────────────────────
  sabloaneMentenanta: ({ tipSistemId, inactive } = {}) => {
    const p = new URLSearchParams();
    if (tipSistemId) p.set("tipSistem", tipSistemId);
    if (inactive) p.set("inactive", "true");
    const s = p.toString();
    return cerere(`/mentenanta/sabloane${s ? "?" + s : ""}`);
  },
  sablonMentenanta: (id) => cerere(`/mentenanta/sabloane/${id}`),
  creeazaSablon: (d) => cerere("/mentenanta/sabloane", { method: "POST", body: JSON.stringify(d) }),
  actualizeazaSablon: (id, d) =>
    cerere(`/mentenanta/sabloane/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  versiuneNouaSablon: (id) =>
    cerere(`/mentenanta/sabloane/${id}/versiune-noua`, { method: "POST" }),
  /** Versiune nouă care aduce și rândurile apărute între timp în setul livrat. */
  versiuneDinStandard: (id) =>
    cerere(`/mentenanta/sabloane/${id}/versiune-din-standard`, { method: "POST" }),
  comutaSablon: (id, activ) =>
    cerere(`/mentenanta/sabloane/${id}/${activ ? "reactiveaza" : "dezactiveaza"}`, { method: "PATCH" }),

  planuriMentenanta: ({ clientId, inactive } = {}) => {
    const p = new URLSearchParams();
    if (clientId) p.set("client", clientId);
    if (inactive) p.set("inactive", "true");
    const s = p.toString();
    return cerere(`/mentenanta/planuri${s ? "?" + s : ""}`);
  },
  scadentarMentenanta: (zile = 30) => cerere(`/mentenanta/scadentar?zile=${zile}`),
  creeazaPlan: (d) => cerere("/mentenanta/planuri", { method: "POST", body: JSON.stringify(d) }),
  actualizeazaPlan: (id, d) =>
    cerere(`/mentenanta/planuri/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  genereazaVizite: () => cerere("/mentenanta/genereaza-vizite", { method: "POST" }),

  checklist: (vizitaId) => cerere(`/mentenanta/checklist/${vizitaId}`),
  sabloanePentruVizita: (vizitaId) => cerere(`/mentenanta/checklist/${vizitaId}/sabloane`),
  deschideChecklist: (vizitaId, sablonId) =>
    cerere(`/mentenanta/checklist/${vizitaId}`, { method: "POST", body: JSON.stringify({ sablonId }) }),
  completeazaChecklist: (vizitaId, d) =>
    cerere(`/mentenanta/checklist/${vizitaId}`, { method: "PATCH", body: JSON.stringify(d) }),
  finalizeazaChecklist: (vizitaId, d) =>
    cerere(`/mentenanta/checklist/${vizitaId}/finalizeaza`, { method: "PATCH", body: JSON.stringify(d) }),

  // ── Backlog tehnic (MT25) ───────────────────────────────────────────────
  // ── MT23 — estimat vs. realizat ──────────────────────────────────────
  bilantFisa: (fisaId) => cerere(`/abateri/fisa/${fisaId}`),
  abateriPerOperatie: (zile = 365) => cerere(`/abateri/per-operatie?zile=${zile}`),
  abateriPerTehnician: (zile = 180, tehnicianId) =>
    cerere(`/abateri/per-tehnician?zile=${zile}${tehnicianId ? `&tehnician=${tehnicianId}` : ""}`),
  abateriPerClient: (zile = 365) => cerere(`/abateri/per-client?zile=${zile}`),
  normePropuse: (status) => cerere(`/abateri/norme-propuse${status ? `?status=${status}` : ""}`),
  recalculeazaNorme: (corp = {}) =>
    cerere("/abateri/recalculeaza", { method: "POST", body: JSON.stringify(corp) }),
  acceptaNorma: (id, corp) =>
    cerere(`/abateri/norme-propuse/${id}/accepta`, { method: "PATCH", body: JSON.stringify(corp) }),
  respingeNorma: (id, corp) =>
    cerere(`/abateri/norme-propuse/${id}/respinge`, { method: "PATCH", body: JSON.stringify(corp) }),

  // ── Regimul de preventiv și prețurile negociate ──────────────────────
  regimPreventiv: (clientId) => cerere(`/clienti/${clientId}/regim-preventiv`),
  preturiClient: (clientId, toate = false) =>
    cerere(`/catalog/preturi-client/${clientId}${toate ? "?toate=true" : ""}`),
  aprobaPretClient: (id) => cerere(`/catalog/preturi-client/${id}/aproba`, { method: "POST" }),
  respingePretClient: (id, motiv) =>
    cerere(`/catalog/preturi-client/${id}/respinge`, { method: "POST", body: JSON.stringify({ motiv }) }),
  pragDiscount: () => cerere("/catalog/prag-discount"),
  setPragDiscount: (prag) =>
    cerere("/catalog/prag-discount", { method: "PATCH", body: JSON.stringify({ prag }) }),
  aseazaPretClient: (corp) =>
    cerere("/catalog/preturi-client", { method: "POST", body: JSON.stringify(corp) }),
  incheiePretClient: (id) =>
    cerere(`/catalog/preturi-client/${id}/incheie`, { method: "PATCH" }),

  // ── MT26 — contorul de abonament ─────────────────────────────────────
  pachete: ({ clientId, contractId, inactive } = {}) => {
    const p = new URLSearchParams();
    if (clientId) p.set("client", clientId);
    if (contractId) p.set("contract", contractId);
    if (inactive) p.set("inactive", "true");
    const q = p.toString();
    return cerere(`/consum-contract/pachete${q ? `?${q}` : ""}`);
  },
  contorClient: (clientId) => cerere(`/consum-contract/client/${clientId}`),
  soldPachet: (id) => cerere(`/consum-contract/pachete/${id}`),
  creeazaPachet: (corp) =>
    cerere("/consum-contract/pachete", { method: "POST", body: JSON.stringify(corp) }),
  actualizeazaPachet: (id, corp) =>
    cerere(`/consum-contract/pachete/${id}`, { method: "PATCH", body: JSON.stringify(corp) }),

  // ── MT14 — garanții și returi ────────────────────────────────────────
  garantii: ({ clientId, echipamentId, inactive } = {}) => {
    const p = new URLSearchParams();
    if (clientId) p.set("client", clientId);
    if (echipamentId) p.set("echipament", echipamentId);
    if (inactive) p.set("inactive", "true");
    const q = p.toString();
    return cerere(`/garantii${q ? `?${q}` : ""}`);
  },
  garantiiCareExpira: (zile = 60) => cerere(`/garantii/expira?zile=${zile}`),
  creeazaGarantie: (corp) =>
    cerere("/garantii", { method: "POST", body: JSON.stringify(corp) }),
  actualizeazaGarantie: (id, corp) =>
    cerere(`/garantii/${id}`, { method: "PATCH", body: JSON.stringify(corp) }),
  anuntaExpirari: () => cerere("/garantii/anunta-expirari", { method: "POST", body: "{}" }),

  rmauri: ({ clientId, status, echipamentId } = {}) => {
    const p = new URLSearchParams();
    if (clientId) p.set("client", clientId);
    if (status) p.set("status", status);
    if (echipamentId) p.set("echipament", echipamentId);
    const q = p.toString();
    return cerere(`/rma${q ? `?${q}` : ""}`);
  },
  rma: (id) => cerere(`/rma/${id}`),
  deschideRma: (corp) => cerere("/rma", { method: "POST", body: JSON.stringify(corp) }),
  trimiteRma: (id, corp) =>
    cerere(`/rma/${id}/trimite`, { method: "PATCH", body: JSON.stringify(corp) }),
  statusRma: (id, corp) =>
    cerere(`/rma/${id}/status`, { method: "PATCH", body: JSON.stringify(corp) }),
  inchideRma: (id, corp) =>
    cerere(`/rma/${id}/inchide`, { method: "PATCH", body: JSON.stringify(corp) }),

  restante: ({ clientId, status, motiv, responsabilId, locatieId, vechimeMin } = {}) => {
    const p = new URLSearchParams();
    if (clientId) p.set("client", clientId);
    if (status) p.set("status", status);
    if (motiv) p.set("motiv", motiv);
    if (responsabilId) p.set("responsabil", responsabilId);
    if (locatieId) p.set("locatie", locatieId);
    if (vechimeMin) p.set("vechimeMin", String(vechimeMin));
    const s = p.toString();
    return cerere(`/restante${s ? "?" + s : ""}`);
  },
  restantePeLocatie: (locatieId) => cerere(`/restante/locatie/${locatieId}`),
  raportPeCauza: (luni = 12) => cerere(`/restante/raport/pe-cauza?luni=${luni}`),
  restanta: (id) => cerere(`/restante/${id}`),
  creeazaRestanta: (d) => cerere("/restante", { method: "POST", body: JSON.stringify(d) }),
  actualizeazaRestanta: (id, d) =>
    cerere(`/restante/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  rezolvaRestanta: (id, fisaId) =>
    cerere(`/restante/${id}/rezolva`, { method: "PATCH", body: JSON.stringify({ fisaId }) }),
  anuleazaRestanta: (id, motiv) =>
    cerere(`/restante/${id}/anuleaza`, { method: "PATCH", body: JSON.stringify({ motiv }) }),

  // ── Rapoarte și tablou de bord (MT16) ───────────────────────────────────
  // Filtrul e unul singur pentru toate rapoartele; ce nu se aplică se ignoră.
  ...(() => {
    const q = (f = {}) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(f)) if (v) p.set(k, v);
      const s = p.toString();
      return s ? "?" + s : "";
    };
    const r = (cale) => (f) => cerere(`/rapoarte/${cale}${q(f)}`);
    return {
      rapTablou: r("tablou"),
      rapSla: r("sla"),
      rapTimpi: r("timpi"),
      rapFiabilitate: r("fiabilitate"),
      rapRentabilitate: r("rentabilitate"),
      rapProductivitate: r("productivitate"),
      rapScadentar: r("scadentar"),
      rapConsum: r("consum-materiale"),
      rapGarantii: r("garantii"),
      rapRegistru: r("registru"),
    };
  })(),

  // ── Portalul clientului (MT15) ──────────────────────────────────────────
  // `client` se trimite doar de conturile firmei: pentru un cont de client
  // serverul îl ignoră oricum și rămâne pe propriul client.
  portalRezumat: (client) => cerere(`/portal/rezumat${client ? `?client=${client}` : ""}`),
  portalSisteme: (client) => cerere(`/portal/sisteme${client ? `?client=${client}` : ""}`),
  portalInterventii: (client, { de, pana } = {}) => {
    const p = new URLSearchParams();
    if (client) p.set("client", client);
    if (de) p.set("de", de);
    if (pana) p.set("pana", pana);
    const q = p.toString();
    return cerere(`/portal/interventii${q ? "?" + q : ""}`);
  },
  portalInterventie: (id, client) =>
    cerere(`/portal/interventii/${id}${client ? `?client=${client}` : ""}`),
  portalDocumente: (client) => cerere(`/portal/documente${client ? `?client=${client}` : ""}`),
  portalSla: (client) => cerere(`/portal/rapoarte/sla${client ? `?client=${client}` : ""}`),
  portalCheltuieli: (client) => cerere(`/portal/rapoarte/cheltuieli${client ? `?client=${client}` : ""}`),
  portalEchipamente: (client) => cerere(`/portal/rapoarte/echipamente${client ? `?client=${client}` : ""}`),

  // ── Predarea către facturare (MT12.6 / §39) ─────────────────────────────
  deFacturat: ({ client, deLa, panaLa } = {}) => {
    const p = new URLSearchParams();
    if (client) p.set("client", client);
    if (deLa) p.set("deLa", deLa);
    if (panaLa) p.set("panaLa", panaLa);
    const q = p.toString();
    return cerere(`/facturare/de-facturat${q ? "?" + q : ""}`);
  },
  loturiFacturare: (client) =>
    cerere(`/facturare/loturi${client ? `?client=${client}` : ""}`),
  lotFacturare: (id) => cerere(`/facturare/loturi/${id}`),
  predaLot: (d) => cerere("/facturare/loturi", { method: "POST", body: JSON.stringify(d) }),
  anuleazaLot: (id, motiv) =>
    cerere(`/facturare/loturi/${id}/anuleaza`, { method: "PATCH", body: JSON.stringify({ motiv }) }),
  scrieReferintaFactura: (id, referintaFactura) =>
    cerere(`/facturare/loturi/${id}/referinta`, {
      method: "PATCH", body: JSON.stringify({ referintaFactura }) }),
  // Fișierul coboară prin API cu tokenul de sesiune, deci nu merge cu un
  // simplu <a href>: vine ca blob și e salvat de browser.
  descarcaLotCsv: async (id, numar) => {
    const r = await fetch(`${API_BASE}/facturare/loturi/${id}/csv`, {
      headers: { Authorization: `Bearer ${citesteTokenuri()?.accessToken ?? ""}` },
    });
    if (!r.ok) throw new Error("Descărcarea a eșuat.");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${numar}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  },

  // ── Registrul de conformitate (MT28) ────────────────────────────────────
  sabloaneObligatii: (tara) => cerere(`/conformitate/sabloane${tara ? `?tara=${tara}` : ""}`),
  tariConformitate: () => cerere("/conformitate/tari"),
  obligatii: (clientId, { inactive } = {}) =>
    cerere(`/conformitate/client/${clientId}${inactive ? "?inactive=true" : ""}`),
  registruConformitate: (clientId) => cerere(`/conformitate/client/${clientId}/registru`),
  creeazaObligatie: (d) =>
    cerere("/conformitate/obligatii", { method: "POST", body: JSON.stringify(d) }),
  copiazaObligatie: (clientId, sablonId) =>
    cerere(`/conformitate/client/${clientId}/din-sablon/${sablonId}`, { method: "POST" }),
  actualizeazaObligatie: (id, d) =>
    cerere(`/conformitate/obligatii/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
  versiuniObligatie: (id) => cerere(`/conformitate/obligatii/${id}/versiuni`),
  genereazaInstante: (id) =>
    cerere(`/conformitate/obligatii/${id}/genereaza-instante`, { method: "POST" }),
  verificaInstanta: (id, d) =>
    cerere(`/conformitate/instante/${id}/verifica`, { method: "PATCH", body: JSON.stringify(d) }),
  genereazaDosar: (d) =>
    cerere("/conformitate/dosare", { method: "POST", body: JSON.stringify(d) }),
  dosareConformitate: (clientId) => cerere(`/conformitate/client/${clientId}/dosare`),
  dosarConformitate: (id) => cerere(`/conformitate/dosare/${id}`),

  // ── Dosarul tehnic (MT06) ───────────────────────────────────────────────
  rubriciDocument: () => cerere("/documente/categorii"),
  stareDepozit: () => cerere("/documente/stare-depozit"),
  documente: (clientId, { categorie, toateVersiunile } = {}) => {
    const p = new URLSearchParams();
    if (categorie) p.set("categorie", categorie);
    if (toateVersiunile) p.set("toateVersiunile", "true");
    const s = p.toString();
    return cerere(`/documente/client/${clientId}${s ? "?" + s : ""}`);
  },
  incarcaDocument: (clientId, d) =>
    cerere(`/documente/client/${clientId}`, { method: "POST", body: JSON.stringify(d) }),
  versiuniDocument: (id) => cerere(`/documente/${id}/versiuni`),
  schimbaVizibilitatea: (id, vizibilClient) =>
    cerere(`/documente/${id}/vizibilitate`, { method: "PATCH", body: JSON.stringify({ vizibilClient }) }),
  // Descărcarea trece prin API cu tokenul de sesiune, deci nu se poate face
  // cu un simplu <a href>: fișierul vine ca blob și e salvat de browser.
  /** Aceeași rută ca descărcarea, dar întoarce o adresă locală pentru <img>. */
  previzualizeazaDocument: async (id) => {
    const r = await fetch(`${API_BASE}/documente/${id}/fisier`, {
      headers: { Authorization: `Bearer ${citesteTokenuri()?.accessToken ?? ""}` },
    });
    if (!r.ok) throw new Error("Fotografia nu s-a putut încărca.");
    return URL.createObjectURL(await r.blob());
  },
  descarcaDocument: async (id, denumire) => {
    const r = await fetch(`${API_BASE}/documente/${id}/fisier`, {
      headers: { Authorization: `Bearer ${citesteTokenuri()?.accessToken ?? ""}` },
    });
    if (!r.ok) throw new Error("Descărcarea a eșuat.");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = denumire;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  },
};
